<script>
import AdminAccountsChart from '@/components/admin/AdminAccountsChart';
import AdminActivityChart from '@/components/admin/AdminActivityChart';
import AdminRevenueChart from '@/components/admin/AdminRevenueChart';
import AdminSignupsChart from '@/components/admin/AdminSignupsChart';
import AdminStatsTable from '@/components/admin/AdminStatsTable';
import AdminUserLookup from '@/components/admin/AdminUserLookup';
import ContentBox from '@/components/ui/ContentBox';
import DashboardLayout from '@/components/layouts/dashboard/DashboardLayout';
import SidebarLink from '@/components/layouts/dashboard/SidebarLink';
import SidebarLinkGroup from '@/components/layouts/dashboard/SidebarLinkGroup';
import chartColors from '@/modules/colors/chart-colors';

export default {
  name: 'Admin',
  components: {
    AdminAccountsChart,
    AdminActivityChart,
    AdminRevenueChart,
    AdminSignupsChart,
    AdminStatsTable,
    AdminUserLookup,
    ContentBox,
    DashboardLayout,
    SidebarLink,
    SidebarLinkGroup
  },
  metaInfo() {
    return { title: this.title };
  },
  data() {
    return {
      title: 'Admin',
      breadcrumbs: [{ text: 'Admin' }],
      activeTab: 'overview',
      chartColors,
      dateRange: [this.getDateFromToday(-30), this.getDateFromToday(0)],
      pickerOptions: {
        disabledDate: date => {
          return date > new Date() || date < new Date(2005, 0, 0); // earliest date for GA
        },
        shortcuts: [
          [0, 0, 'Today'],
          [-1, -1, 'Yesterday'],
          [-7, 0, 'Last {days} days'],
          [-14, 0, 'Last {days} days'],
          [-30, 0, 'Last {days} days'],
          [-60, 0, 'Last {days} days'],
          [-90, 0, 'Last {days} days'],
          [-180, 0, 'Last {days} days'],
          [-365, 0, 'Last year']
        ].map(val => {
          const days = val[1] - val[0];
          const start = this.getDateFromToday(val[0]);
          const end = this.getDateFromToday(val[1]);
          const text = this.$t(val[2], { days });
          return {
            text,
            onClick: picker => picker.$emit('pick', [start, end])
          };
        })
      },
      stats: {},
      statusCode: null
    };
  },
  computed: {
    startDate() {
      const date = this.dateRange[0];
      return date instanceof Date ? date.toISOString().split('T')[0] : '';
    },
    endDate() {
      const date = this.dateRange[1];
      return date instanceof Date ? date.toISOString().split('T')[0] : '';
    }
  },
  created() {
    // Fetch initial data
    this.fetchStats()
      .then(() => (this.statusCode = 200))
      .catch(err => (this.statusCode = err.response.status));
  },
  methods: {
    // Returns a date object relative to the current date
    getDateFromToday(numDays) {
      return new Date(new Date().setDate(new Date().getDate() + numDays));
    },

    fetchStats() {
      return this.$http
        .get('/admin/stats', {
          params: {
            startDate: this.startDate,
            endDate: this.endDate
          }
        })
        .then(res => {
          this.stats = res.data.stats;
          this.statusCode = 200;
        });
    },

    handleTabClick() {
      if (this.activeTab === 'users') {
        this.$nextTick(() => this.$refs.userLookup.focus());
      }
    }
  }
};
</script>

<template>
  <dashboard-layout :status-code="statusCode" size="large">
    <!-- Breadcrumbs -->
    <template slot="breadcrumbs">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item v-for="(item, index) in breadcrumbs" :key="index" :to="item.to">
          {{ item.text }}
        </el-breadcrumb-item>
      </el-breadcrumb>
    </template>

    <!-- Sidebar -->
    <template slot="sidebar">
      <!-- Links -->
      <sidebar-link-group title="Links">
        <sidebar-link text="Stripe" href="https://dashboard.stripe.com/" icon="el-icon-fa-external-link" />
      </sidebar-link-group>
    </template>

    <!-- Body -->
    <div class="stats-options">
      <el-date-picker
        ref="datePicker"
        v-model="dateRange"
        :clearable="false"
        :range-separator="$t('to')"
        :picker-options="pickerOptions"
        start-placeholder="Start Date"
        end-placeholder="End Date"
        type="daterange"
        size="small"
        unlink-panels
        class="date-picker"
        @change="fetchStats"
      />
    </div>

    <content-box class="admin-body">
      <el-tabs v-model="activeTab" @tab-click="handleTabClick">
        <!-- Overview -->
        <el-tab-pane label="Overview" name="overview">
          <!-- Revenue chart -->
          <admin-revenue-chart :colors="chartColors" :data="stats.revenue" />

          <el-row :gutters="20">
            <el-col :xs="24" :sm="24" :md="24" :lg="16" :xl="16">
              <!-- Signups chart -->
              <admin-signups-chart :colors="chartColors" :data="stats.signups" />
            </el-col>
            <el-col :xs="24" :sm="24" :md="24" :lg="8" :xl="8">
              <!-- Accounts chart -->
              <admin-accounts-chart :colors="chartColors" :data="stats.accounts" />
            </el-col>
          </el-row>

          <!-- Activity chart -->
          <admin-activity-chart :colors="chartColors" :data="stats.activity" />

          <!-- Stats -->
          <admin-stats-table :stats="stats" />
        </el-tab-pane>

        <!-- Users -->
        <el-tab-pane label="Users" name="users">
          <admin-user-lookup ref="userLookup" />
        </el-tab-pane>
      </el-tabs>
    </content-box>
  </dashboard-layout>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.stats-options {
  text-align: right;
  margin-bottom: 1rem;

  .date-picker {
    width: 14rem;

    /deep/ {
      .el-range-separator {
        width: 1.5rem;
      }

      .el-range__close-icon {
        display: none;
      }
    }
  }

  @include breakpoint('sm-and-down') {
    text-align: center;
    margin-bottom: 1.5rem;
  }
}

// Adjust tabs badge
/deep/ .el-tabs__item .el-badge__content {
  top: 0.5rem;
  right: 0.25rem;
  line-height: 1rem;
}

.stats {
  .stats-item {
    font-size: 2rem;
    text-align: center;
    padding: 1.2rem;
  }

  small {
    display: block;
    color: $--color-gray-40;
    margin-top: 0.25rem;
  }
}
</style>
